import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Edit, Trash, RotateCcw, Save, X, Check, Lightbulb } from 'lucide-react';

const ContributionViewer = (props) => {
  // Get params from URL and props
  const params = useParams();
  const urlTherapistName = params.therapistName;
  const urlContributionId = params.contributionId;
  
  // Use props or URL params, with props taking precedence
  const therapistName = props.therapistName || urlTherapistName;
  const contributionId = props.contributionId || urlContributionId;
  
  const navigate = useNavigate();
  
  const [contribution, setContribution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modified, setModified] = useState(false);
  const [editTimestamp, setEditTimestamp] = useState(null);
  
  
  // Editing states
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingInsight, setEditingInsight] = useState(null);
  const [convertingQuestion, setConvertingQuestion] = useState(null);
  
  // Edited content
  const [editedQuestionText, setEditedQuestionText] = useState('');
  const [editedQuestionReasoning, setEditedQuestionReasoning] = useState('');
  const [editedQuestionAudience, setEditedQuestionAudience] = useState('');
  const [editedInsightText, setEditedInsightText] = useState('');
  const [editedInsightApplication, setEditedInsightApplication] = useState('');

  // Log parameters for debugging
  useEffect(() => {
    console.log("ContributionViewer - Current parameters:", {
      propsTherapistName: props.therapistName,
      urlTherapistName,
      effectiveTherapistName: therapistName,
      urlContributionId,
      effectiveContributionId: contributionId
    });
  }, [props.therapistName, urlTherapistName, therapistName, urlContributionId, contributionId]);

  // Capitalize therapist name for display
  const displayName = therapistName ? 
    therapistName.charAt(0).toUpperCase() + therapistName.slice(1) : 
    'Unknown';

  // Fetch the contribution when component mounts
  useEffect(() => {
    if (therapistName && contributionId) {
      fetchContribution();
    } else {
      setError(`Missing parameters: therapistName=${therapistName}, contributionId=${contributionId}`);
      setLoading(false);
    }
  }, [therapistName, contributionId]);

  // Fetch contribution from the API
  const fetchContribution = async () => {
    try {
      setLoading(true);
      
      // Add cache-busting query parameter to avoid 304 responses
      const timestamp = new Date().getTime();
      const response = await fetch(
        `/api/get-contribution/${therapistName}/${contributionId}?_=${timestamp}`, 
        {
          // Force fetch to bypass cache
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }
      );
      
      console.log('Response status:', response.status);
      
      // Get the text response first for debugging
      const responseText = await response.text();
      console.log('Response text (first 100 chars):', responseText.substring(0, 100));
      
      // Check for empty response
      if (!responseText || responseText.trim() === '') {
        throw new Error('Empty response received from server');
      }
      
      // Try to parse the response as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('JSON parse error:', e);
        throw new Error(`Failed to parse server response as JSON: ${e.message}`);
      }
      
      if (!response.ok) {
        throw new Error(data.error || `Server returned status ${response.status}`);
      }
      
      if (!data.contribution) {
        throw new Error('Server response missing contribution data');
      }
      
      setContribution(data.contribution);
      setError(null);
      setModified(false);
    } catch (error) {
      console.error('Error fetching contribution:', error);
      setError(`Failed to load the contribution: ${error.message}`);
      setContribution(null);
    } finally {
      setLoading(false);
    }
  };

  // Start editing a question
  const handleEditQuestion = (question, index) => {
    setEditingQuestion(index);
    setEditedQuestionText(question.question);
    setEditedQuestionReasoning(question.reasoning);
    setEditedQuestionAudience(question.audience || 'client');
  };

  // Helper function to mark as modified
  const markAsModified = () => {
    if (!modified) {
      setModified(true);
      setEditTimestamp(new Date().toISOString());
    }
  };

  // Save edited question
  const handleSaveQuestion = (index) => {
    if (!contribution) return;
    
    const updatedQuestions = [...contribution.questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      question: editedQuestionText,
      reasoning: editedQuestionReasoning,
      audience: editedQuestionAudience,
      // Maintain the MongoDB _id if it exists
      _id: updatedQuestions[index]._id || undefined
    };
    
    setContribution({
      ...contribution,
      questions: updatedQuestions,
      lastEdited: editTimestamp || new Date().toISOString()
    });
    
    setEditingQuestion(null);
    markAsModified();
  };

  // Cancel question editing
  const handleCancelQuestionEdit = () => {
    setEditingQuestion(null);
    setEditedQuestionText('');
    setEditedQuestionReasoning('');
    setEditedQuestionAudience('');
  };

  // Start editing an insight
  const handleEditInsight = (insight, index) => {
    setEditingInsight(index);
    setEditedInsightText(insight.insight);
    setEditedInsightApplication(insight.application);
  };

  // Save edited insight
  const handleSaveInsight = (index) => {
    if (!contribution) return;
    
    const updatedInsights = [...contribution.analysis];
    updatedInsights[index] = {
      ...updatedInsights[index],
      insight: editedInsightText,
      application: editedInsightApplication,
      // Maintain the MongoDB _id if it exists
      _id: updatedInsights[index]._id || undefined
    };
    
    setContribution({
      ...contribution,
      analysis: updatedInsights
    });
    
    setEditingInsight(null);
    setModified(true);
  };

  // Cancel insight editing
  const handleCancelInsightEdit = () => {
    setEditingInsight(null);
    setEditedInsightText('');
    setEditedInsightApplication('');
  };

  // Remove a question
  const handleRemoveQuestion = (index) => {
    if (!contribution) return;
    
    if (window.confirm('Are you sure you want to remove this question?')) {
      const updatedQuestions = [...contribution.questions];
      updatedQuestions.splice(index, 1);
      
      // Keep track of deleted question IDs
      const deletedQuestion = contribution.questions[index];
      const deletedIds = [...(contribution.deletedQuestionIds || [])];
      
      if (deletedQuestion._id) {
        deletedIds.push(deletedQuestion._id);
      }
      
      setContribution({
        ...contribution,
        questions: updatedQuestions,
        deletedQuestionIds: deletedIds // This will be sent to backend on save
      });
      
      setModified(true);
    }
  };

  // Remove an insight
  const handleRemoveInsight = (index) => {
    if (!contribution) return;
    
    if (window.confirm('Are you sure you want to remove this insight?')) {
      const insightToRemove = contribution.analysis[index];
      const updatedInsights = [...contribution.analysis];
      updatedInsights.splice(index, 1);
      
      // Track MongoDB IDs for backend deletion
      const deletedIds = contribution.deletedInsightIds || [];
      if (insightToRemove._id) {
        deletedIds.push(insightToRemove._id);
      }
      
      setContribution({
        ...contribution,
        analysis: updatedInsights,
        deletedInsightIds: deletedIds
      });
      
      setModified(true);
    }
  };

  // Start converting a question to an insight
  const handleConvertQuestion = (question, index) => {
    setConvertingQuestion(index);
    
    // Include the MongoDB ID in the conversion request if available
    const questionWithId = question._id ? 
      { ...question, id: question._id } : 
      question;
      
    convertQuestionToInsight(questionWithId, index);
  };

  // Process converting a question to an insight
  const convertQuestionToInsight = async (question, index) => {
    try {
      const response = await fetch('/api/convert-question-to-insight', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: question,
          contributionId: contributionId // Pass the contribution ID for database updates
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to convert question to insight');
      }
      
      // Add the new insight with its MongoDB ID if provided
      const newInsight = data.insight._id ? 
        data.insight : 
        { ...data.insight, _id: undefined };
        
      const updatedAnalysis = [...(contribution.analysis || []), newInsight];
      
      // Remove the converted question
      const updatedQuestions = [...contribution.questions];
      updatedQuestions.splice(index, 1);
      
      // Track deleted question ID if available
      const deletedIds = contribution.deletedQuestionIds || [];
      if (question._id) {
        deletedIds.push(question._id);
      }
      
      // Update the contribution
      setContribution({
        ...contribution,
        questions: updatedQuestions,
        analysis: updatedAnalysis,
        deletedQuestionIds: deletedIds,
        lastEdited: editTimestamp || new Date().toISOString()
      });
      
      markAsModified();
    } catch (error) {
      console.error('Error converting question to insight:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setConvertingQuestion(null);
    }
  };

  // Save all changes to the contribution
  const handleSaveContribution = async () => {
    if (!contribution) return;
    
    try {
      setSaving(true);
      
      // Ensure the contribution has all required fields
      const cleanedContribution = {
        ...contribution,
        questions: contribution.questions || [],
        analysis: contribution.analysis || [],
        summary: contribution.summary || "No summary provided",
        timestamp: contribution.timestamp || new Date().toISOString().replace('T', ' ').substring(0, 19),
        lastEdited: contribution.lastEdited || editTimestamp || new Date().toISOString(),
        // Include deleted IDs arrays for backend processing
        deletedQuestionIds: contribution.deletedQuestionIds || [],
        deletedInsightIds: contribution.deletedInsightIds || []
      };
      
      // Ensure all questions have audience field
      cleanedContribution.questions = cleanedContribution.questions.map(question => ({
        ...question,
        audience: question.audience && ['client', 'therapist'].includes(question.audience)
          ? question.audience
          : 'client' // Default to client if missing or invalid
      }));
      
      const response = await fetch(`/api/update-contribution/${therapistName}/${contributionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contribution: cleanedContribution
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save contribution');
      }
      
      alert('Contribution saved successfully!');
      setModified(false);
      
      // Refresh the contribution to get the latest data
      await fetchContribution();
    } catch (error) {
      console.error('Error saving contribution:', error);
      alert(`Error saving contribution: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-50 flex flex-col">
      {/* Header */}
      <header className="bg-blue-600 text-white p-4 shadow-md">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate(`/${therapistName}`)}
              className="mr-4 p-1 hover:bg-blue-700 rounded-full"
            >
              <ChevronLeft size={24} />
            </button>
            <h1 className="text-xl md:text-2xl font-bold">
              Dr. {displayName}'s Contribution
            </h1>
          </div>
          <div className="flex items-center">
            {modified && (
              <div className="mr-3 text-xs bg-yellow-500 px-2 py-1 rounded-full">
                Unsaved Changes
              </div>
            )}
            <button
              onClick={handleSaveContribution}
              disabled={!modified || saving}
              className={`flex items-center px-3 py-1 rounded-full text-sm ${
                modified && !saving 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-gray-500 cursor-not-allowed'
              }`}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-1" /> Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow container mx-auto py-8 px-4">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error:</strong>
            <span className="block sm:inline"> {error}</span>
          </div>
        ) : contribution ? (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-gray-800">
                  Contribution Details
                </h2>
                {contribution.lastEdited && (
                  <div className="text-sm text-orange-600 font-medium mt-1">
                    Edited on {new Date(contribution.lastEdited).toLocaleDateString()} at {new Date(contribution.lastEdited).toLocaleTimeString()}
                  </div>
                )}
              </div>
              <div className="text-gray-500">
                {contribution.timestamp}
              </div>
            </div>
            
            {/* Summary */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Summary</h3>
              <p className="text-gray-700">{contribution.summary}</p>
            </div>
            
            {/* Processed Categories */}
            <div className="space-y-6">
              {/* Questions - separated by audience */}
              {contribution.questions && contribution.questions.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-3">
                    Questions ({contribution.questions.length})
                  </h3>
                  
                  {/* Client Questions */}
                  {contribution.questions.filter(q => q.audience === 'client').length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-md font-medium text-blue-700 mb-2 flex items-center">
                        <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                        Client Questions ({contribution.questions.filter(q => q.audience === 'client').length})
                      </h4>
                      <div className="space-y-3">
                        {contribution.questions
                          .filter(item => item.audience === 'client')
                          .map((item, index) => {
                            const globalIndex = contribution.questions.findIndex(q => q === item);
                            return editingQuestion === globalIndex ? (
                              <div key={`qc-edit-${index}`} className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                                <div className="mb-2">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Question:</label>
                                  <textarea 
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                    value={editedQuestionText}
                                    onChange={(e) => setEditedQuestionText(e.target.value)}
                                  />
                                </div>
                                <div className="mb-2">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Reasoning:</label>
                                  <textarea 
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                    value={editedQuestionReasoning}
                                    onChange={(e) => setEditedQuestionReasoning(e.target.value)}
                                  />
                                </div>
                                <div className="mb-3">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Audience:</label>
                                  <select
                                    className="p-2 border border-gray-300 rounded-md"
                                    value={editedQuestionAudience}
                                    onChange={(e) => setEditedQuestionAudience(e.target.value)}
                                  >
                                    <option value="client">Client</option>
                                    <option value="therapist">Therapist</option>
                                  </select>
                                </div>
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={handleCancelQuestionEdit}
                                    className="flex items-center px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100"
                                  >
                                    <X size={16} className="mr-1" /> Cancel
                                  </button>
                                  <button
                                    onClick={() => handleSaveQuestion(globalIndex)}
                                    className="flex items-center px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                  >
                                    <Check size={16} className="mr-1" /> Save
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div key={`qc-${index}`} className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                                <div className="font-medium mb-1">{item.question}</div>
                                <div className="text-sm text-gray-600 mb-2">Reasoning: {item.reasoning}</div>
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={() => handleEditQuestion(item, globalIndex)}
                                    className="flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
                                  >
                                    <Edit size={14} className="mr-1" /> Edit
                                  </button>
                                  <button
                                    onClick={() => handleConvertQuestion(item, globalIndex)}
                                    disabled={convertingQuestion === globalIndex}
                                    className={`flex items-center px-2 py-1 text-xs ${
                                      convertingQuestion === globalIndex
                                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                        : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                    } rounded-md`}
                                  >
                                    {convertingQuestion === globalIndex ? (
                                      <>
                                        <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-purple-700 mr-1"></div>
                                        Converting...
                                      </>
                                    ) : (
                                      <>
                                        <Lightbulb size={14} className="mr-1" /> To Insight
                                      </>
                                    )}
                                  </button>
                                  <button
                                    onClick={() => handleRemoveQuestion(globalIndex)}
                                    className="flex items-center px-2 py-1 text-xs bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                                  >
                                    <Trash size={14} className="mr-1" /> Remove
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                  
                  {/* Therapist Questions */}
                  {contribution.questions.filter(q => q.audience === 'therapist').length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-md font-medium text-green-700 mb-2 flex items-center">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        Therapist Questions ({contribution.questions.filter(q => q.audience === 'therapist').length})
                      </h4>
                      <div className="space-y-3">
                        {contribution.questions
                          .filter(item => item.audience === 'therapist')
                          .map((item, index) => {
                            const globalIndex = contribution.questions.findIndex(q => q === item);
                            return editingQuestion === globalIndex ? (
                              <div key={`qt-edit-${index}`} className="p-3 bg-green-50 border border-green-200 rounded-md">
                                <div className="mb-2">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Question:</label>
                                  <textarea 
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                    value={editedQuestionText}
                                    onChange={(e) => setEditedQuestionText(e.target.value)}
                                  />
                                </div>
                                <div className="mb-2">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Reasoning:</label>
                                  <textarea 
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                    value={editedQuestionReasoning}
                                    onChange={(e) => setEditedQuestionReasoning(e.target.value)}
                                  />
                                </div>
                                <div className="mb-3">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Audience:</label>
                                  <select
                                    className="p-2 border border-gray-300 rounded-md"
                                    value={editedQuestionAudience}
                                    onChange={(e) => setEditedQuestionAudience(e.target.value)}
                                  >
                                    <option value="client">Client</option>
                                    <option value="therapist">Therapist</option>
                                  </select>
                                </div>
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={handleCancelQuestionEdit}
                                    className="flex items-center px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100"
                                  >
                                    <X size={16} className="mr-1" /> Cancel
                                  </button>
                                  <button
                                    onClick={() => handleSaveQuestion(globalIndex)}
                                    className="flex items-center px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700"
                                  >
                                    <Check size={16} className="mr-1" /> Save
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div key={`qt-${index}`} className="p-3 bg-green-50 border border-green-200 rounded-md">
                                <div className="font-medium mb-1">{item.question}</div>
                                <div className="text-sm text-gray-600 mb-2">Reasoning: {item.reasoning}</div>
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={() => handleEditQuestion(item, globalIndex)}
                                    className="flex items-center px-2 py-1 text-xs bg-green-100 text-green-700 rounded-md hover:bg-green-200"
                                  >
                                    <Edit size={14} className="mr-1" /> Edit
                                  </button>
                                  <button
                                    onClick={() => handleConvertQuestion(item, globalIndex)}
                                    disabled={convertingQuestion === globalIndex}
                                    className={`flex items-center px-2 py-1 text-xs ${
                                      convertingQuestion === globalIndex
                                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                        : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                    } rounded-md`}
                                  >
                                    {convertingQuestion === globalIndex ? (
                                      <>
                                        <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-purple-700 mr-1"></div>
                                        Converting...
                                      </>
                                    ) : (
                                      <>
                                        <Lightbulb size={14} className="mr-1" /> To Insight
                                      </>
                                    )}
                                  </button>
                                  <button
                                    onClick={() => handleRemoveQuestion(globalIndex)}
                                    className="flex items-center px-2 py-1 text-xs bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                                  >
                                    <Trash size={14} className="mr-1" /> Remove
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                  
                  {/* Uncategorized Questions (fallback) */}
                  {contribution.questions.filter(q => !q.audience || (q.audience !== 'client' && q.audience !== 'therapist')).length > 0 && (
                    <div>
                      <h4 className="text-md font-medium text-gray-700 mb-2 flex items-center">
                        <span className="inline-block w-2 h-2 bg-gray-500 rounded-full mr-2"></span>
                        Other Questions ({contribution.questions.filter(q => !q.audience || (q.audience !== 'client' && q.audience !== 'therapist')).length})
                      </h4>
                      <div className="space-y-3">
                        {contribution.questions
                          .filter(item => !item.audience || (item.audience !== 'client' && item.audience !== 'therapist'))
                          .map((item, index) => {
                            const globalIndex = contribution.questions.findIndex(q => q === item);
                            return editingQuestion === globalIndex ? (
                              <div key={`qo-edit-${index}`} className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                <div className="mb-2">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Question:</label>
                                  <textarea 
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                    value={editedQuestionText}
                                    onChange={(e) => setEditedQuestionText(e.target.value)}
                                  />
                                </div>
                                <div className="mb-2">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Reasoning:</label>
                                  <textarea 
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                    value={editedQuestionReasoning}
                                    onChange={(e) => setEditedQuestionReasoning(e.target.value)}
                                  />
                                </div>
                                <div className="mb-3">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Audience:</label>
                                  <select
                                    className="p-2 border border-gray-300 rounded-md"
                                    value={editedQuestionAudience}
                                    onChange={(e) => setEditedQuestionAudience(e.target.value)}
                                  >
                                    <option value="client">Client</option>
                                    <option value="therapist">Therapist</option>
                                  </select>
                                </div>
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={handleCancelQuestionEdit}
                                    className="flex items-center px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100"
                                  >
                                    <X size={16} className="mr-1" /> Cancel
                                  </button>
                                  <button
                                    onClick={() => handleSaveQuestion(globalIndex)}
                                    className="flex items-center px-3 py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                                  >
                                    <Check size={16} className="mr-1" /> Save
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div key={`qo-${index}`} className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                <div className="font-medium mb-1">{item.question}</div>
                                <div className="text-sm text-gray-600 mb-2">Reasoning: {item.reasoning}</div>
                                {item.audience && <div className="text-xs text-gray-500 mb-2">Audience: {item.audience}</div>}
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={() => handleEditQuestion(item, globalIndex)}
                                    className="flex items-center px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                                  >
                                    <Edit size={14} className="mr-1" /> Edit
                                  </button>
                                  <button
                                    onClick={() => handleConvertQuestion(item, globalIndex)}
                                    disabled={convertingQuestion === globalIndex}
                                    className={`flex items-center px-2 py-1 text-xs ${
                                      convertingQuestion === globalIndex
                                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                        : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                    } rounded-md`}
                                  >
                                    {convertingQuestion === globalIndex ? (
                                      <>
                                        <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-purple-700 mr-1"></div>
                                        Converting...
                                      </>
                                    ) : (
                                      <>
                                        <Lightbulb size={14} className="mr-1" /> To Insight
                                      </>
                                    )}
                                  </button>
                                  <button
                                    onClick={() => handleRemoveQuestion(globalIndex)}
                                    className="flex items-center px-2 py-1 text-xs bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                                  >
                                    <Trash size={14} className="mr-1" /> Remove
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Analysis */}
              {contribution.analysis && contribution.analysis.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-3">
                    Analysis Insights ({contribution.analysis.length})
                  </h3>
                  <div className="space-y-3">
                    {contribution.analysis.map((item, index) => (
                      editingInsight === index ? (
                        <div key={`a-edit-${index}`} className="p-3 bg-purple-50 border border-purple-200 rounded-md">
                          <div className="mb-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Insight:</label>
                            <textarea 
                              className="w-full p-2 border border-gray-300 rounded-md"
                              value={editedInsightText}
                              onChange={(e) => setEditedInsightText(e.target.value)}
                            />
                          </div>
                          <div className="mb-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Application:</label>
                            <textarea 
                              className="w-full p-2 border border-gray-300 rounded-md"
                              value={editedInsightApplication}
                              onChange={(e) => setEditedInsightApplication(e.target.value)}
                            />
                          </div>
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={handleCancelInsightEdit}
                              className="flex items-center px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100"
                            >
                              <X size={16} className="mr-1" /> Cancel
                            </button>
                            <button
                              onClick={() => handleSaveInsight(index)}
                              className="flex items-center px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                            >
                              <Check size={16} className="mr-1" /> Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div key={`a-${index}`} className="p-3 bg-purple-50 border border-purple-200 rounded-md">
                          <div className="font-medium mb-1">{item.insight}</div>
                          <div className="text-sm text-gray-600 mb-2">Application: {item.application}</div>
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleEditInsight(item, index)}
                              className="flex items-center px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200"
                            >
                              <Edit size={14} className="mr-1" /> Edit
                            </button>
                            <button
                              onClick={() => handleRemoveInsight(index)}
                              className="flex items-center px-2 py-1 text-xs bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                            >
                              <Trash size={14} className="mr-1" /> Remove
                            </button>
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Save changes button */}
            {modified && (
              <div className="mt-6 text-center">
                <button
                  onClick={handleSaveContribution}
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-400"
                >
                  {saving ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></div>
                      Saving Changes...
                    </>
                  ) : (
                    'Save All Changes'
                  )}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Not Found:</strong>
            <span className="block sm:inline"> The requested contribution could not be found.</span>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-blue-800 text-blue-200 py-4 text-center text-sm">
        <div className="container mx-auto">
          Therapist Knowledge Capture System | Mindaera
        </div>
      </footer>
    </div>
  );
};

export default ContributionViewer;